import React, { useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField
} from "@material-ui/core";
import { toast } from "react-toastify";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import formatDefaultValueForDatetimeInput from "../../utils/datetime/formatDefaultValueForDatetimeInputs";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  selectInputField: {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.text.primary, },
    "& .MuiSvgIcon-root": { color: theme.palette.text.primary, },
	},

  floatingButton: {
    transition: "all 0.30s ease",
    "&:hover": { transform: "translateY(-5px)", },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50.00%",
    left: "50.00%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const BulkResolveTicketsModal = ({ open, onClose }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const [isSubmitting, setIsSubmitting] = useState(false);



  //  ***************
  //  ** Use Memos **
  //  ***************
  const dataJsonMemo = useMemo(() => ({
    status: 2
    , initialDate: formatDefaultValueForDatetimeInput()
    , finalDate: formatDefaultValueForDatetimeInput()
  }), []);

  const [dataJson, setDataJson] = useState(dataJsonMemo);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleClose = () => {
    onClose();
    setIsSubmitting(false);
    setTimeout(() => setDataJson(dataJsonMemo), 300);
  };

  const handleChangeData = (event) => {
    const { name, value } = event.target;
    setDataJson(previousValue => ({ ...previousValue, [name]: value }));
  };

  const handleResolveTickets = async () => {
    try {
      setIsSubmitting(true);

      if (![0, 1, 2].includes(dataJson.status)) {
        toast.info(i18n.t("bulkResolveTicketsModal.toasts.invalidStatus"));
        return;
      }

      if (new Date(dataJson.initialDate) > new Date(dataJson.finalDate)) {
        toast.info(i18n.t("bulkResolveTicketsModal.toasts.invalidInitialDate"));
        return;
      }
      
      const { data } = await api.patch("/bulkActions/closeTickets", dataJson);
      if (data.success) toast.success(i18n.t("bulkResolveTicketsModal.toasts.success"));
      else toast.info(i18n.t(`bulkResolveTicketsModal.toasts.${data.message}`));
    }
    catch (exception) {
      console.log("handleResolveTickets exception:", exception);
      toastError(exception);
    }
    finally {
      handleClose();
    }
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" scroll="paper" fullWidth>
      <DialogTitle>
        {i18n.t("bulkActionsModals.titles.closeTickets")}
      </DialogTitle>

      <DialogContent dividers>
        <FormControl className={classes.selectInputField} variant="outlined" margin="dense" fullWidth>
          <InputLabel>{i18n.t("bulkResolveTicketsModal.form.status")}</InputLabel>

          <Select
            name="status"
            label={i18n.t("bulkResolveTicketsModal.form.status")}
            value={dataJson.status}
            onChange={event => handleChangeData(event)}
            fullWidth
          >
            <MenuItem key={"pending"} value={2}>{i18n.t("bulkResolveTicketsModal.form.statusValues.pending")}</MenuItem>
            <MenuItem key={"open"} value={1}>{i18n.t("bulkResolveTicketsModal.form.statusValues.open")}</MenuItem>
            <MenuItem key={"both"} value={0}>{i18n.t("bulkResolveTicketsModal.form.statusValues.both")}</MenuItem>
          </Select>
        </FormControl>

        <TextField
          className={classes.selectInputField}
          label={i18n.t("bulkResolveTicketsModal.form.initialDate")}
          name="initialDate"
          variant="outlined"
          margin="normal"
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
          value={dataJson.initialDate}
          onChange={event => handleChangeData(event)}
          fullWidth
        />

        <TextField
          className={classes.selectInputField}
          label={i18n.t("bulkResolveTicketsModal.form.finalDate")}
          name="finalDate"
          variant="outlined"
          margin="normal"
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
          value={dataJson.finalDate}
          onChange={event => handleChangeData(event)}
          fullWidth
        />
      </DialogContent>

      <DialogActions>
        <Button
          className={classes.floatingButton}
          disabled={isSubmitting}
          onClick={handleClose}
          color="inherit"
          variant="outlined"
        >
          {i18n.t("bulkActionsModals.buttons.cancel")}
        </Button>

        <Button
          className={`${classes.floatingButton} ${classes.btnWrapper}`}
          disabled={isSubmitting}
          onClick={handleResolveTickets}
          color="primary"
          variant="contained"
        >
          {i18n.t("bulkActionsModals.buttons.confirm")}
          {isSubmitting && (<CircularProgress size={24} className={classes.buttonProgress} />)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkResolveTicketsModal;
